import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Search, Trash2, UtensilsCrossed } from 'lucide-react';
import clsx from 'clsx';
import {
  DESTINATION_LABELS,
  PRODUCTION_DESTINATIONS,
  TAX_TYPES,
  formatAmount,
  formatRate,
  parseAmountToCents,
  type MenuItem,
  type ProductionDestination,
  type TaxType,
} from '@sebastian/shared';
import { api, ApiError } from '../lib/api';
import { useCategories, useMenuItems, useModifierGroups, useSettings } from '../hooks/queries';
import { useToast } from '../hooks/useToast';
import { useCurrency } from '../lib/currency';
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Modal,
  Select,
  Spinner,
  Textarea,
  Toggle,
} from '../components/ui';

interface Draft {
  id?: string;
  categoryId: string;
  name: string;
  description: string;
  price: string;
  taxType: TaxType;
  destination: ProductionDestination;
  sku: string;
  unit: string;
  prepMinutes: string;
  isActive: boolean;
  trackStock: boolean;
  stockQuantity: string;
  modifierGroupIds: string[];
}

const EMPTY: Draft = {
  categoryId: '',
  name: '',
  description: '',
  price: '0.00',
  taxType: 'FOOD',
  destination: 'KITCHEN',
  sku: '',
  unit: '',
  prepMinutes: '',
  isActive: true,
  trackStock: false,
  stockQuantity: '',
  modifierGroupIds: [],
};

/** Menu item administration: price, tax class, routing and reusable options. */
export function MenuAdminPage() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { money, symbol, decimals } = useCurrency();

  const { data: items, isLoading, error, refetch } = useMenuItems(true);
  const { data: categories = [] } = useCategories(true);
  const { data: groups = [] } = useModifierGroups(true);
  const { data: settings } = useSettings();

  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [draft, setDraft] = useState<Draft | null>(null);
  const [removing, setRemoving] = useState<MenuItem | null>(null);

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['menu-items'] });
  const failed = (caught: unknown, fallback: string) =>
    toast.error(caught instanceof ApiError ? caught.message : fallback);

  const save = useMutation({
    mutationFn: (value: Draft) => {
      const body = {
        categoryId: value.categoryId,
        name: value.name.trim(),
        description: value.description.trim() || null,
        priceCents: parseAmountToCents(value.price),
        taxType: value.taxType,
        destination: value.destination,
        sku: value.sku.trim() || null,
        unit: value.unit.trim() || null,
        prepMinutes: value.prepMinutes ? Number(value.prepMinutes) : null,
        isActive: value.isActive,
        trackStock: value.trackStock,
        stockQuantity: value.trackStock ? Number(value.stockQuantity || 0) : null,
        modifierGroupIds: value.modifierGroupIds,
      };
      return value.id ? api.menuItems.update(value.id, body) : api.menuItems.create(body);
    },
    onSuccess: () => {
      invalidate();
      setDraft(null);
      toast.success('Menu item saved. Existing orders keep their original price.');
    },
    onError: (caught) => failed(caught, 'Could not save that item.'),
  });

  /** Fast restock during service, without opening the full edit form. */
  const [restocking, setRestocking] = useState<MenuItem | null>(null);
  const [restockValue, setRestockValue] = useState('0');
  const restock = useMutation({
    mutationFn: (value: { id: string; stockQuantity: number }) =>
      api.menuItems.update(value.id, { stockQuantity: value.stockQuantity }),
    onSuccess: () => {
      invalidate();
      setRestocking(null);
      toast.success('Stock updated.');
    },
    onError: (caught) => failed(caught, 'Could not update stock.'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.menuItems.remove(id),
    onSuccess: (result) => {
      invalidate();
      setRemoving(null);
      toast.success(
        result.deactivated
          ? 'Item deactivated — it has order history, so the record is kept.'
          : 'Item deleted.',
      );
    },
    onError: (caught) => failed(caught, 'Could not remove that item.'),
  });

  const visible = useMemo(() => {
    const query = search.trim().toLowerCase();
    return (items ?? []).filter((item) => {
      if (categoryFilter && item.categoryId !== categoryFilter) return false;
      if (!query) return true;
      return (
        item.name.toLowerCase().includes(query) ||
        item.sku?.toLowerCase().includes(query) ||
        item.categoryName?.toLowerCase().includes(query)
      );
    });
  }, [items, search, categoryFilter]);

  const taxRateFor = (taxType: TaxType) =>
    taxType === 'ALCOHOL' ? (settings?.alcoholTaxBp ?? 1000) : (settings?.foodTaxBp ?? 800);

  return (
    <div className="mx-auto max-w-[1400px] px-5 py-6">
      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl tracking-wide text-ink">Menu</h1>
          <p className="mt-1 text-sm text-ink-faint">
            Each item declares its own tax class and production station — nothing is inferred from
            the category name.
          </p>
        </div>
        <Button
          variant="primary"
          icon={<Plus size={16} />}
          onClick={() => setDraft({ ...EMPTY, categoryId: categories[0]?.id ?? '' })}
        >
          New item
        </Button>
      </header>

      <div className="mb-5 flex flex-wrap gap-3">
        <div className="relative min-w-64 flex-1">
          <Search size={17} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-faint" />
          <Input
            className="pl-10"
            placeholder="Search items…"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
        <Select
          className="w-56"
          value={categoryFilter}
          onChange={(event) => setCategoryFilter(event.target.value)}
        >
          <option value="">All categories</option>
          {categories.map((category) => (
            <option key={category.id} value={category.id}>
              {category.name}
            </option>
          ))}
        </Select>
      </div>

      {isLoading ? <Spinner /> : null}
      {error ? <ErrorState error={error} onRetry={() => void refetch()} /> : null}

      {!isLoading && visible.length === 0 ? (
        <EmptyState icon={<UtensilsCrossed size={30} />} title="No items" message="Nothing matches." />
      ) : null}

      {visible.length > 0 ? (
        <Card padded={false} className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-sm">
            <thead className="bg-raised/60 text-left text-[11px] uppercase tracking-wider text-ink-faint">
              <tr>
                <th className="px-4 py-3 font-semibold">Item</th>
                <th className="px-4 py-3 font-semibold">Category</th>
                <th className="px-4 py-3 font-semibold">Tax</th>
                <th className="px-4 py-3 font-semibold">Station</th>
                <th className="px-4 py-3 font-semibold">Options</th>
                <th className="px-4 py-3 font-semibold">Stock</th>
                <th className="px-4 py-3 text-right font-semibold">Price</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {visible.map((item) => (
                <tr key={item.id} className="border-t border-line hover:bg-hover">
                  <td className="px-4 py-3">
                    <div className={clsx('font-medium', item.isActive ? 'text-ink' : 'text-ink-faint')}>
                      {item.name}
                      {!item.isActive ? <Badge className="ml-2" tone="neutral">Inactive</Badge> : null}
                    </div>
                    {item.sku ? <div className="text-xs text-ink-faint">{item.sku}</div> : null}
                  </td>
                  <td className="px-4 py-3 text-ink-soft">{item.categoryName}</td>
                  <td className="px-4 py-3">
                    <Badge tone={item.taxType === 'ALCOHOL' ? 'caution' : 'neutral'}>
                      {item.taxType === 'ALCOHOL' ? 'Alcohol' : 'Food'} {formatRate(taxRateFor(item.taxType))}%
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    <Badge tone={item.destination === 'NONE' ? 'neutral' : 'info'}>
                      {DESTINATION_LABELS[item.destination]}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-xs text-ink-faint">
                    {item.modifierGroups.map((group) => group.name).join(', ') || '—'}
                  </td>
                  <td className="px-4 py-3">
                    {item.trackStock ? (
                      <button
                        type="button"
                        onClick={() => {
                          setRestocking(item);
                          setRestockValue(String(item.stockQuantity ?? 0));
                        }}
                        title="Click to set stock"
                      >
                        <Badge tone={(item.stockQuantity ?? 0) <= 0 ? 'danger' : (item.stockQuantity ?? 0) <= 5 ? 'caution' : 'neutral'}>
                          {item.stockQuantity ?? 0} left
                        </Badge>
                      </button>
                    ) : (
                      <span className="text-xs text-ink-faint">Unlimited</span>
                    )}
                  </td>
                  <td className="tnum px-4 py-3 text-right font-semibold text-accent-strong">
                    {money(item.priceCents)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <button
                        type="button"
                        aria-label="Edit"
                        onClick={() =>
                          setDraft({
                            id: item.id,
                            categoryId: item.categoryId,
                            name: item.name,
                            description: item.description ?? '',
                            price: formatAmount(item.priceCents, decimals),
                            taxType: item.taxType,
                            destination: item.destination,
                            sku: item.sku ?? '',
                            unit: item.unit ?? '',
                            prepMinutes: item.prepMinutes ? String(item.prepMinutes) : '',
                            isActive: item.isActive,
                            trackStock: item.trackStock,
                            stockQuantity: item.stockQuantity !== null ? String(item.stockQuantity) : '0',
                            modifierGroupIds: item.modifierGroups.map((group) => group.id),
                          })
                        }
                        className="rounded p-1.5 text-ink-faint transition hover:text-accent"
                      >
                        <Pencil size={15} />
                      </button>
                      <button
                        type="button"
                        aria-label="Remove"
                        onClick={() => setRemoving(item)}
                        className="rounded p-1.5 text-ink-faint transition hover:text-danger"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      ) : null}

      <Modal
        open={draft !== null}
        onClose={() => setDraft(null)}
        title={draft?.id ? 'Edit menu item' : 'New menu item'}
        size="lg"
        footer={
          <>
            <Button variant="ghost" onClick={() => setDraft(null)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={save.isPending}
              disabled={!draft?.name.trim() || !draft?.categoryId}
              onClick={() => draft && save.mutate(draft)}
            >
              Save item
            </Button>
          </>
        }
      >
        {draft ? (
          <div className="space-y-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Name">
                <Input
                  autoFocus
                  value={draft.name}
                  onChange={(event) => setDraft({ ...draft, name: event.target.value })}
                />
              </Field>
              <Field label="Category">
                <Select
                  value={draft.categoryId}
                  onChange={(event) => setDraft({ ...draft, categoryId: event.target.value })}
                >
                  <option value="">Choose a category…</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>

            <Field label="Description">
              <Textarea
                rows={2}
                value={draft.description}
                onChange={(event) => setDraft({ ...draft, description: event.target.value })}
              />
            </Field>

            <div className="grid gap-4 sm:grid-cols-3">
              <Field label="Price">
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-faint">
                    {symbol}
                  </span>
                  <Input
                    inputMode="decimal"
                    className="pl-8"
                    value={draft.price}
                    onChange={(event) => setDraft({ ...draft, price: event.target.value })}
                  />
                </div>
              </Field>
              <Field
                label="Tax class"
                hint={`Currently ${formatRate(taxRateFor(draft.taxType))}%`}
              >
                <Select
                  value={draft.taxType}
                  onChange={(event) => setDraft({ ...draft, taxType: event.target.value as TaxType })}
                >
                  {TAX_TYPES.map((value) => (
                    <option key={value} value={value}>
                      {value === 'ALCOHOL' ? 'Alcohol' : 'Food / Non-alcoholic'}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Production station">
                <Select
                  value={draft.destination}
                  onChange={(event) =>
                    setDraft({ ...draft, destination: event.target.value as ProductionDestination })
                  }
                >
                  {PRODUCTION_DESTINATIONS.map((value) => (
                    <option key={value} value={value}>
                      {DESTINATION_LABELS[value]}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="SKU / internal code">
                <Input
                  value={draft.sku}
                  onChange={(event) => setDraft({ ...draft, sku: event.target.value })}
                  placeholder="MAI-001"
                />
              </Field>
              <Field label="Invoice unit" hint="Đơn vị tính on the e-invoice, e.g. Phần, Ly, Chai. Blank uses the default from Settings.">
                <Input
                  value={draft.unit}
                  onChange={(event) => setDraft({ ...draft, unit: event.target.value })}
                  placeholder="Phần"
                />
              </Field>
              <Field label="Prep time (minutes)">
                <Input
                  type="number"
                  value={draft.prepMinutes}
                  onChange={(event) => setDraft({ ...draft, prepMinutes: event.target.value })}
                />
              </Field>
            </div>

            <div>
              <span className="mb-2 block text-xs font-semibold uppercase tracking-wider text-ink-faint">
                Modifier groups
              </span>
              <div className="flex flex-wrap gap-2">
                {groups.length === 0 ? (
                  <p className="text-sm text-ink-faint">No modifier groups defined yet.</p>
                ) : null}
                {groups.map((group) => {
                  const selected = draft.modifierGroupIds.includes(group.id);
                  return (
                    <button
                      key={group.id}
                      type="button"
                      onClick={() =>
                        setDraft({
                          ...draft,
                          modifierGroupIds: selected
                            ? draft.modifierGroupIds.filter((id) => id !== group.id)
                            : [...draft.modifierGroupIds, group.id],
                        })
                      }
                      className={clsx(
                        'h-10 rounded-lg border px-3 text-sm transition',
                        selected
                          ? 'border-accent bg-accent/12 text-accent-strong'
                          : 'border-line text-ink-soft hover:border-line-strong hover:text-ink',
                      )}
                    >
                      {group.name}
                      {group.isRequired ? <span className="ml-1 text-[10px] uppercase">req</span> : null}
                    </button>
                  );
                })}
              </div>
            </div>

            <Toggle
              checked={draft.isActive}
              onChange={(isActive) => setDraft({ ...draft, isActive })}
              label="Available to order"
            />

            <div className="space-y-3 rounded-lg border border-line bg-canvas px-4 py-3.5">
              <Toggle
                checked={draft.trackStock}
                onChange={(trackStock) => setDraft({ ...draft, trackStock })}
                label="Track stock"
              />
              {draft.trackStock ? (
                <Field label="Quantity available" hint="Decreases as orders are placed; voiding an item gives it back.">
                  <Input
                    type="number"
                    min={0}
                    className="max-w-32"
                    value={draft.stockQuantity}
                    onChange={(event) => setDraft({ ...draft, stockQuantity: event.target.value })}
                  />
                </Field>
              ) : (
                <p className="text-xs text-ink-faint">Off — unlimited, the item never runs out.</p>
              )}
            </div>
          </div>
        ) : null}
      </Modal>

      <Modal
        open={restocking !== null}
        onClose={() => setRestocking(null)}
        title={`Set stock — ${restocking?.name ?? ''}`}
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setRestocking(null)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={restock.isPending}
              onClick={() =>
                restocking && restock.mutate({ id: restocking.id, stockQuantity: Number(restockValue || 0) })
              }
            >
              Save
            </Button>
          </>
        }
      >
        <Field label="Quantity available">
          <Input
            type="number"
            min={0}
            autoFocus
            value={restockValue}
            onChange={(event) => setRestockValue(event.target.value)}
          />
        </Field>
      </Modal>

      <ConfirmDialog
        open={removing !== null}
        title={`Remove "${removing?.name ?? ''}"?`}
        message="If the item has ever been ordered it is deactivated rather than deleted, so historical orders stay intact."
        confirmLabel="Remove"
        loading={remove.isPending}
        onCancel={() => setRemoving(null)}
        onConfirm={() => removing && remove.mutate(removing.id)}
      />
    </div>
  );
}
